import type { StorageService } from "../types/platform";
import type { LicenseAdapter, LicenseFlags } from "../types/license";

export const LOCAL_LICENSE_STORAGE_KEYS = {
  gatingEnabled: "exiftrator-license-gating-enabled",
  proUnlocked: "exiftrator-license-pro-unlocked",
  bypassEnabled: "exiftrator-license-bypass-enabled",
} as const;

const DEFAULT_FLAGS: LicenseFlags = {
  gatingEnabled: false,
  proUnlocked: false,
  bypassEnabled: false,
};

function parseStoredBoolean(value: string | null): boolean {
  return value === "1";
}

function serializeBoolean(value: boolean): string {
  return value ? "1" : "0";
}

function resolveStorageKeys(prefix?: string) {
  if (!prefix) {
    return LOCAL_LICENSE_STORAGE_KEYS;
  }

  return {
    gatingEnabled: `${prefix}-gating-enabled`,
    proUnlocked: `${prefix}-pro-unlocked`,
    bypassEnabled: `${prefix}-bypass-enabled`,
  } as const;
}

export function createLocalLicenseAdapter(
  storage: StorageService,
  options: {
    allowBypass?: boolean;
    defaults?: Partial<LicenseFlags>;
    storageKeyPrefix?: string;
  } = {},
): LicenseAdapter {
  const allowBypass = Boolean(options.allowBypass);
  const defaults: LicenseFlags = {
    ...DEFAULT_FLAGS,
    ...options.defaults,
  };
  const storageKeys = resolveStorageKeys(options.storageKeyPrefix);

  return {
    allowBypass,
    async load() {
      const [gatingEnabled, proUnlocked, bypassEnabled] = await Promise.all([
        storage.getItem(storageKeys.gatingEnabled),
        storage.getItem(storageKeys.proUnlocked),
        storage.getItem(storageKeys.bypassEnabled),
      ]);

      return {
        gatingEnabled:
          gatingEnabled === null
            ? defaults.gatingEnabled
            : parseStoredBoolean(gatingEnabled),
        proUnlocked:
          proUnlocked === null
            ? defaults.proUnlocked
            : parseStoredBoolean(proUnlocked),
        bypassEnabled:
          allowBypass &&
          (bypassEnabled === null
            ? defaults.bypassEnabled
            : parseStoredBoolean(bypassEnabled)),
      };
    },
    async setGatingEnabled(enabled: boolean) {
      await storage.setItem(
        storageKeys.gatingEnabled,
        serializeBoolean(enabled),
      );
    },
    async setProUnlocked(enabled: boolean) {
      await storage.setItem(
        storageKeys.proUnlocked,
        serializeBoolean(enabled),
      );
    },
    async setBypassEnabled(enabled: boolean) {
      if (!allowBypass) {
        await storage.removeItem(storageKeys.bypassEnabled);
        return;
      }

      await storage.setItem(
        storageKeys.bypassEnabled,
        serializeBoolean(enabled),
      );
    },
  };
}

export function createFallbackLicenseAdapter(): LicenseAdapter {
  return {
    allowBypass: false,
    async load() {
      return DEFAULT_FLAGS;
    },
    async setGatingEnabled() {},
    async setProUnlocked() {},
    async setBypassEnabled() {},
  };
}
